import { queryClient } from '@quoruum/frontend-common/api/queryClient';
import { LOCATION_OPTIONS } from '@quoruum/frontend-common/oas/gen/enumValues.gen';
import {
  locationGetOptions,
  locationSaveMutation,
} from '@quoruum/frontend-common/oas/gen/quoruum/@tanstack/react-query.gen';
import type {
  LocationBean,
  LocationBeanWritable,
  WithDeleteBeanLocationOptionWritable,
} from '@quoruum/frontend-common/oas/gen/quoruum/types.gen';
import { useMutation } from '@tanstack/react-query';
import type { ReactElement, ReactNode } from 'react';
import type {
  EditableFieldSpec,
  EditableFieldValue,
  EntityDetailRow,
} from '../../components/EditableEntityTable';
import { EditableEntityTable } from '../../components/EditableEntityTable';
import { formatUnknownValue } from './hotelMapShared';

type EditableLocationFieldKey = 'options';

type LocationOptionValue = NonNullable<WithDeleteBeanLocationOptionWritable['value']>;
const LOCATION_OPTION_VALUES = LOCATION_OPTIONS;

const LOCATION_OPTION_SET = new Set<string>(LOCATION_OPTION_VALUES);

function isLocationOptionValue(value: string): value is LocationOptionValue {
  return LOCATION_OPTION_SET.has(value);
}

const LOCATION_DISPLAY_KEYS: Array<keyof LocationBean> = [
  'id',
  'name',
  'code',
  'latitude',
  'longitude',
  'parent',
  'options',
  'auditBean',
];

type EditableLocationFieldDefinition = {
  key: EditableLocationFieldKey;
  label: string;
  editSpec: EditableFieldSpec;
  getValue: (location: LocationBean) => Array<LocationOptionValue>;
  renderReadOnlyValue: (value: Array<LocationOptionValue>) => ReactNode;
  buildMutationBody: (
    locationId: string,
    value: Array<LocationOptionValue>,
    originalValue: Array<LocationOptionValue>
  ) => Pick<LocationBeanWritable, 'id' | 'options'>;
};

const EDITABLE_LOCATION_FIELD_DEFINITIONS: Array<EditableLocationFieldDefinition> = [
  {
    key: 'options',
    label: 'options',
    editSpec: {
      editType: 'multiselect-enum',
      options: LOCATION_OPTION_VALUES,
    },
    getValue: (location) => {
      const options = location.options;
      if (!Array.isArray(options)) {
        return [];
      }

      const extractedOptions: Array<LocationOptionValue> = [];
      for (const option of options) {
        if (!option || typeof option !== 'object') {
          continue;
        }

        const optionValue = (option as { value?: unknown }).value;
        if (typeof optionValue === 'string' && isLocationOptionValue(optionValue)) {
          extractedOptions.push(optionValue);
        }
      }

      return extractedOptions;
    },
    renderReadOnlyValue: (value) => (value.length > 0 ? value.join(', ') : '-'),
    buildMutationBody: (locationId, value, originalValue) => {
      const selectedOptions = new Set(value.filter(isLocationOptionValue));
      const removedOptions = originalValue
        .filter(isLocationOptionValue)
        .filter((option) => !selectedOptions.has(option));

      return {
        id: locationId,
        options: [
          ...Array.from(selectedOptions).map((option) => ({ value: option })),
          ...removedOptions.map((option) => ({ value: option, deleted: true })),
        ],
      };
    },
  },
];

const EDITABLE_LOCATION_FIELD_BY_KEY = new Map(
  EDITABLE_LOCATION_FIELD_DEFINITIONS.map((field) => [field.key, field] as const)
);

const LOCATION_EDITABLE_FIELD_SPECS: ReadonlyMap<EditableLocationFieldKey, EditableFieldSpec> =
  new Map(EDITABLE_LOCATION_FIELD_DEFINITIONS.map((def) => [def.key, def.editSpec]));

function buildLocationDetailsRows(
  location: LocationBean
): Array<EntityDetailRow<EditableLocationFieldKey>> {
  const rows: Array<EntityDetailRow<EditableLocationFieldKey>> = [];
  const includedLocationKeys = new Set<string>();
  const includedEditableFields = new Set<EditableLocationFieldKey>();

  const pushRow = (key: string, value: unknown): void => {
    includedLocationKeys.add(key);

    const editableFieldDefinition = EDITABLE_LOCATION_FIELD_BY_KEY.get(
      key as EditableLocationFieldKey
    );
    if (editableFieldDefinition) {
      const editableFieldArrayValue = editableFieldDefinition.getValue(location);
      rows.push({
        id: key,
        key: editableFieldDefinition.label,
        value: editableFieldDefinition.renderReadOnlyValue(editableFieldArrayValue),
        editableFieldKey: editableFieldDefinition.key,
        editableFieldArrayValue,
      });
      includedEditableFields.add(editableFieldDefinition.key);
      return;
    }

    if (key === 'options') {
      rows.push({
        id: key,
        key,
        value: renderLocationOptions(value),
      });
      return;
    }

    rows.push({
      id: key,
      key,
      value: formatUnknownValue(value),
    });
  };

  for (const key of LOCATION_DISPLAY_KEYS) {
    pushRow(key, location[key]);
  }

  for (const [key, value] of Object.entries(location)) {
    if (includedLocationKeys.has(key)) {
      continue;
    }

    pushRow(key, value);
  }

  for (const editableFieldDefinition of EDITABLE_LOCATION_FIELD_DEFINITIONS) {
    if (includedEditableFields.has(editableFieldDefinition.key)) {
      continue;
    }

    const editableFieldArrayValue = editableFieldDefinition.getValue(location);
    rows.push({
      id: editableFieldDefinition.key,
      key: editableFieldDefinition.label,
      value: editableFieldDefinition.renderReadOnlyValue(editableFieldArrayValue),
      editableFieldKey: editableFieldDefinition.key,
      editableFieldArrayValue,
    });
  }

  return rows;
}

function renderLocationOptions(value: unknown): ReactNode {
  if (!Array.isArray(value)) {
    return formatUnknownValue(value);
  }

  if (value.length === 0) {
    return '-';
  }

  return (
    <ul className="list-inside list-disc space-y-1 pl-2">
      {value.map((site, index) => (
        <li key={index} className="text-xs text-slate-800">
          {typeof site === 'string' ? site : JSON.stringify(site)}
        </li>
      ))}
    </ul>
  );
}

type CitySectionProps = {
  selectedCityId: string | undefined;
  selectedLocationByIdResponse: LocationBean | undefined;
};

export function CitySection({
  selectedCityId,
  selectedLocationByIdResponse,
}: CitySectionProps): ReactElement {
  const saveLocationMutation = useMutation(locationSaveMutation());
  async function handleSaveLocationField(
    fieldKey: EditableLocationFieldKey,
    value: EditableFieldValue
  ): Promise<void> {
    if (!selectedCityId) return;

    const fieldDef = EDITABLE_LOCATION_FIELD_BY_KEY.get(fieldKey);
    if (!fieldDef) return;

    if (!Array.isArray(value)) {
      throw new Error('Invalid value for selected field.');
    }

    const selectedOptions = value.filter(
      (item): item is LocationOptionValue => typeof item === 'string' && isLocationOptionValue(item)
    );
    if (selectedOptions.length !== value.length) {
      throw new Error('Invalid location options selected.');
    }

    const originalValue = selectedLocationByIdResponse
      ? fieldDef.getValue(selectedLocationByIdResponse)
      : [];

    await saveLocationMutation.mutateAsync({
      path: { id: selectedCityId },
      body: fieldDef.buildMutationBody(selectedCityId, selectedOptions, originalValue),
    });

    const refreshedLocationDetails = await queryClient.fetchQuery(
      locationGetOptions({ path: { id: selectedCityId } })
    );
    // Update the location data in the query cache and trigger re-render
    const newData = { ...selectedLocationByIdResponse, ...refreshedLocationDetails };
    queryClient.setQueryData(
      locationGetOptions({ path: { id: selectedCityId } }).queryKey,
      newData
    );
  }

  return (
    <div className="min-h-0 flex-1 overflow-y-auto rounded-md border border-slate-200">
      {selectedCityId && selectedLocationByIdResponse ? (
        <EditableEntityTable
          key={selectedCityId}
          rows={buildLocationDetailsRows(selectedLocationByIdResponse)}
          fieldDefinitions={LOCATION_EDITABLE_FIELD_SPECS}
          onSave={handleSaveLocationField}
        />
      ) : null}
    </div>
  );
}
